import { IconChevronDown } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type PageNav, pageWindow, type ServerPageNav } from '../lib/paginate';
import { buttonClass } from './ui/Button';

// Pager for a paged list (#924). Same voice as the existing "Load more" on
// the archive surfaces — `.btn--outline .btn--sm`, `aria-disabled` (never the
// `disabled` attribute, #5264 review) at the ends — with a mono page counter,
// since every other numeral in the console is mono (stat band, table cells,
// timestamps).
//
// Two modes share one shape:
//   - The next/prev/count row is the CLIENT-SIDE mode. A client-side
//     `Pagination<T>` already satisfies the `PageNav` prop, so the three
//     existing callers (`routes/Home.tsx`, `routes/Pending.tsx`,
//     `components/FleetQueuePanel.tsx`) keep working with ZERO changes.
//   - When `pagination` is a `ServerPageNav` — `total`/`pageSize`/`goTo`
//     riding on the SAME object `serverPageNav` returns, not a second set of
//     sibling props — the count between prev and next is replaced by
//     numbered page buttons (from `pageWindow`, with an ellipsis for each
//     gap), so the row does not overflow at 360px, followed by a
//     "Showing a–b of N" line after next. A plain `PageNav` falls back to
//     the client-side row BYTE-IDENTICALLY, count included.
//
// `goTo` alone is the discriminant: a `PageNav` never carries one, so there
// is no partial state to misrender and no `total`/`pageSize` prop that could
// disagree with the ones the nav object itself carries.
//
// Renders NOTHING when the list fits on one page (`pageCount <= 1`): a dead
// pager under a three-row panel is chrome that only ever says "no".
//
// ponytail: the chevrons are the existing IconChevronDown rotated in CSS. The
// icon set has no left/right chevron and this needs no new asset — swap to real
// ones if the set grows them.

export function Pager(props: {
  readonly pagination: PageNav | ServerPageNav;
  /** Names the list for assistive tech, e.g. "Agent fleet". */
  readonly label: string;
}): JSX.Element {
  const p = (): PageNav | ServerPageNav => props.pagination;
  // `goTo` only exists on a `ServerPageNav` — its presence IS numbered mode,
  // with no second prop that could disagree with it.
  const serverNav = (): ServerPageNav | undefined => {
    const nav = p();
    return 'goTo' in nav ? nav : undefined;
  };
  return (
    <Show when={!p().single()}>
      <nav class="pager" aria-label={t('pager.aria', { label: props.label })}>
        <button
          type="button"
          class={`${buttonClass({ variant: 'outline', size: 'icon-sm' })} pager__step pager__step--prev`}
          aria-label={t('pager.prev')}
          // `aria-disabled`, not `disabled` (#5264 review, mirrors the
          // numbered-page button below): `disabled` drops a button out of
          // the tab order, so reaching page 1 — including via the very
          // click that lands you there — would strand a keyboard reader on
          // <body>. Shared `.btn[aria-disabled='true']` styling
          // (styles/ui/_button.scss) paints the identical dimmed,
          // unclickable look as `:disabled` in BOTH modes this component
          // renders, so this is visually a no-op change.
          aria-disabled={p().page() <= 1 ? 'true' : undefined}
          onClick={() => {
            // The click guard is still required: `pointer-events: none` and
            // `aria-disabled` are presentational only, not blocking — a
            // dispatched click (or one from a keyboard activation) still
            // fires `onClick`.
            if (p().page() > 1) p().prev();
          }}
        >
          <IconChevronDown size="0.875rem" />
        </button>
        {/* Numbered mode: the number buttons sit BETWEEN prev and next, and
            the "N / M" count is gone (the buttons already say it) — in
            client mode the count is the only thing here. Whichever mode is
            active, this is the ONE `aria-live` region in that mode: two live
            regions both changing on the same click would announce the page
            change twice. */}
        <Show
          when={serverNav()}
          fallback={
            <span class="pager__count" aria-live="polite">
              {t('pager.count', { page: String(p().page()), total: String(p().pageCount()) })}
            </span>
          }
        >
          {(nav) => (
            <For each={pageWindow(p().page(), p().pageCount())}>
              {(entry) => {
                if (entry === 'gap') {
                  return (
                    <span class="pager__gap" aria-hidden="true">
                      {t('pager.gap')}
                    </span>
                  );
                }
                return (
                  <button
                    type="button"
                    class={`${buttonClass({ variant: 'outline', size: 'icon-sm' })} pager__num`}
                    aria-label={t('pager.page_aria', { page: String(entry) })}
                    aria-current={entry === p().page() ? 'page' : undefined}
                    // `aria-disabled`, not the `disabled` attribute: a `disabled`
                    // button drops out of the tab order, so a keyboard reader who
                    // just activated this page loses focus to <body> the instant
                    // it becomes current. `.btn[aria-disabled='true']`
                    // (styles/ui/_button.scss) already paints the same dimmed,
                    // unclickable look as `:disabled`. The click guard below is
                    // still required: `pointer-events: none` and `aria-disabled`
                    // are presentational only — neither stops `onClick` firing
                    // from a keyboard activation or a dispatched click event.
                    aria-disabled={entry === p().page() ? 'true' : undefined}
                    onClick={() => {
                      if (entry !== p().page()) nav().goTo(entry);
                    }}
                  >
                    {entry}
                  </button>
                );
              }}
            </For>
          )}
        </Show>
        <button
          type="button"
          class={`${buttonClass({ variant: 'outline', size: 'icon-sm' })} pager__step pager__step--next`}
          aria-label={t('pager.next')}
          // `aria-disabled`, not `disabled` — see the prev button's comment.
          aria-disabled={p().page() >= p().pageCount() ? 'true' : undefined}
          onClick={() => {
            if (p().page() < p().pageCount()) p().next();
          }}
        >
          <IconChevronDown size="0.875rem" />
        </button>
        {/* "Showing a–b of N" (#5136), numbered mode only — totals come from
            the envelope (`PlanListPage.total`), not the page's own slice
            length, so the line tracks what the server has, never what the
            window holds. */}
        <Show when={serverNav()}>
          {(nav) => (
            <span class="pager__showing" aria-live="polite">
              {t('pager.showing', {
                from: String((p().page() - 1) * nav().pageSize() + 1),
                to: String(Math.min(nav().total(), p().page() * nav().pageSize())),
                total: String(nav().total()),
              })}
            </span>
          )}
        </Show>
      </nav>
    </Show>
  );
}
